import type { LabelFieldSourceType, LabelLayoutType } from '@aeris/contracts'
import { measuresOf, sheetGeometry, type LabelMeasures } from '@aeris/printing'

/**
 * O rascunho do editor de modelo de etiqueta — F129.
 *
 * O editor é o "Gerador de Etiquetas" do GDOOR: uma aba de **Medidas** e uma
 * tabela de **Campos**. O que a pessoa digita nas medidas não é o que o modelo
 * guarda — o modelo guarda a folha resolvida —, então o rascunho mantém as
 * medidas como foram digitadas e só vira layout na hora de desenhar ou salvar.
 *
 * Funções puras, sem React: a regra de "o que acontece quando eu mudo isto"
 * tem teste sem montar tela nenhuma.
 */

export type DraftElement = LabelLayoutType['elements'][number]
export type TextElement = Extract<DraftElement, { kind: 'text' }>
export type BarcodeElement = Extract<DraftElement, { kind: 'barcode' }>

export type LabelDraft = {
  readonly name: string
  readonly measures: LabelMeasures
  readonly elements: readonly DraftElement[]
}

/** A bobina do exemplo do GDOOR: 110 mm, três adesivos de 33 × 22 por linha. */
export const BLANK_MEASURES: LabelMeasures = {
  paper: 'roll',
  pageWidthMm: 110,
  pageHeightMm: 297,
  marginLeftMm: 3,
  marginRightMm: 3,
  marginTopMm: 2,
  marginBottomMm: 0,
  labelWidthMm: 33,
  labelHeightMm: 22,
  columnGapMm: 2,
  rowGapMm: 0,
  columns: 3,
  rows: 1,
}

/** Folha A4, para quem troca o tipo do papel. */
export const A4 = { pageWidthMm: 210, pageHeightMm: 297 } as const

export const FIELD_LABELS: Record<LabelFieldSourceType, string> = {
  sku: 'Código',
  name: 'Descrição',
  salePrice: 'Preço de venda',
  installment: 'Parcelado',
  gtin: 'Código de barras (número)',
  brand: 'Marca',
  unit: 'Unidade',
  lastPurchaseAt: 'Última compra',
  printedAt: 'Data da impressão',
  supplierReference: 'Identificação do fornecedor',
  location: 'Localização da peça',
  literal: 'Texto fixo',
}

export const emptyDraft = (): LabelDraft => ({
  name: '',
  measures: BLANK_MEASURES,
  elements: [],
})

export const draftFromLayout = (
  name: string,
  layout: LabelLayoutType
): LabelDraft => ({
  name,
  measures: measuresOf(layout),
  elements: layout.elements,
})

export const layoutFromDraft = (draft: LabelDraft): LabelLayoutType => ({
  name: draft.name.trim() === '' ? 'Modelo sem nome' : draft.name.trim(),
  ...sheetGeometry(draft.measures),
  elements: [...draft.elements],
})

export const newTextElement = (
  source: LabelFieldSourceType = 'name'
): TextElement => ({
  kind: 'text',
  source,
  text: source === 'literal' ? 'Texto' : '',
  ...(source === 'installment' ? { installments: 10 } : {}),
  leftMm: 1,
  topMm: 1,
  widthMm: 30,
  heightMm: 5,
  fontFamily: 'Arial',
  fontSizePt: 8,
  color: '#000000',
  background: null,
  bold: false,
  italic: false,
  underline: false,
  align: 'left',
  wrap: false,
  rotationDeg: 0,
})

export const newBarcodeElement = (): BarcodeElement => ({
  kind: 'barcode',
  leftMm: 1,
  topMm: 10,
  widthMm: 30,
  heightMm: 8,
  moduleWidth: 2,
  showText: true,
  color: '#000000',
})

export type DraftAction =
  | { readonly type: 'reset'; readonly draft: LabelDraft }
  | { readonly type: 'rename'; readonly name: string }
  | { readonly type: 'measures'; readonly patch: Partial<LabelMeasures> }
  | { readonly type: 'add'; readonly element: DraftElement }
  | {
      readonly type: 'text'
      readonly index: number
      readonly patch: Partial<Omit<TextElement, 'kind'>>
    }
  | {
      readonly type: 'barcode'
      readonly index: number
      readonly patch: Partial<Omit<BarcodeElement, 'kind'>>
    }
  | { readonly type: 'remove'; readonly index: number }
  | {
      readonly type: 'nudge'
      readonly index: number
      readonly dxMm: number
      readonly dyMm: number
    }
  | { readonly type: 'duplicate'; readonly index: number }

/** Sem dízima de ponto flutuante: 0,1 + 0,2 não pode virar 0,30000000000000004. */
const roundMm = (value: number): number => Math.round(value * 1000) / 1000

/**
 * Como o campo aparece na lista e no painel.
 *
 * O texto fixo mostra o começo do texto — seis "Texto fixo" iguais na lista é
 * o mesmo defeito da tabela que esta tela substituiu.
 */
export const describeElement = (element: DraftElement): string => {
  if (element.kind === 'barcode') return 'Código de barras'
  if (element.source === 'literal') {
    const first = element.text.split('\n')[0]?.trim() ?? ''
    if (first === '') return 'Texto fixo'
    return `Texto fixo: ${first.length > 24 ? `${first.slice(0, 24)}…` : first}`
  }
  if (element.source === 'installment') {
    return `Parcelado em ${String(element.installments ?? 10)}X`
  }
  return FIELD_LABELS[element.source]
}

export const draftReducer = (
  draft: LabelDraft,
  action: DraftAction
): LabelDraft => {
  switch (action.type) {
    case 'reset':
      return action.draft
    case 'rename':
      return { ...draft, name: action.name }
    case 'measures': {
      const measures = { ...draft.measures, ...action.patch }
      /*
       * Trocar para folha põe a medida de uma A4; voltar para bobina não mexe
       * na largura. Uma bobina de 110 mm virando "folha de 110 × 297" é uma
       * folha que não existe, e a prévia pareceria errada sem estar.
       */
      if (action.patch.paper === 'sheet' && draft.measures.paper === 'roll') {
        return {
          ...draft,
          measures: { ...measures, ...A4, rows: Math.max(measures.rows, 50) },
        }
      }
      return { ...draft, measures }
    }
    case 'add':
      return { ...draft, elements: [...draft.elements, action.element] }
    case 'text':
      return {
        ...draft,
        elements: draft.elements.map((element, index) => {
          if (index !== action.index || element.kind !== 'text') return element
          const next: TextElement = { ...element, ...action.patch }
          // Parcelado sem número de vezes não imprime nada: ganha um padrão.
          return next.source === 'installment' &&
            next.installments === undefined
            ? { ...next, installments: 10 }
            : next
        }),
      }
    case 'barcode':
      return {
        ...draft,
        elements: draft.elements.map((element, index) =>
          index === action.index && element.kind === 'barcode'
            ? { ...element, ...action.patch }
            : element
        ),
      }
    case 'remove':
      return {
        ...draft,
        elements: draft.elements.filter((_, index) => index !== action.index),
      }
    case 'nudge':
      return {
        ...draft,
        elements: draft.elements.map((element, index) =>
          index === action.index
            ? {
                ...element,
                leftMm: roundMm(element.leftMm + action.dxMm),
                topMm: roundMm(element.topMm + action.dyMm),
              }
            : element
        ),
      }
    case 'duplicate': {
      const original = draft.elements[action.index]
      if (original === undefined) return draft
      // A cópia sai 1 mm deslocada: em cima da original, ninguém vê que ela
      // existe e parece que o botão não fez nada.
      const copy: DraftElement = {
        ...original,
        leftMm: roundMm(original.leftMm + 1),
        topMm: roundMm(original.topMm + 1),
      }
      return {
        ...draft,
        elements: [
          ...draft.elements.slice(0, action.index + 1),
          copy,
          ...draft.elements.slice(action.index + 1),
        ],
      }
    }
  }
}

/** Milímetro digitado com vírgula ou ponto. `null` quando não é número. */
export const parseDecimalInput = (text: string): number | null => {
  const trimmed = text.trim()
  if (!/^-?\d+([.,]\d+)?$/.test(trimmed)) return null
  return Number(trimmed.replace(',', '.'))
}

export const formatDecimalInput = (value: number): string =>
  String(Math.round(value * 1000) / 1000).replace('.', ',')
